import xstream from "state-x-stream";
import {
  switchMap,
  map,
  combineLatest,
  distinctUntilChanged,
  BehaviorSubject,
  debounceTime,
} from "rxjs";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import { TAG_BG } from "../../constants/tagConstants";

function controller({ props$ }) {
  let el = null;

  const highlight$ = props$.pipe(
    switchMap(({ highlighted$ }) => highlighted$),
    debounceTime(400),
    distinctUntilChanged((a, b) => a?.typeKey === b?.typeKey),
    map((x) => {
      const isHighlighted = x && props$?.value?.tag?.typeKey === x?.typeKey;
      if (isHighlighted && el && props$.value.isAutoScroll && x?.autoScroll) {
        x.autoScroll = false;
        el.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
      return isHighlighted;
    })
  );

  const select$ = props$.pipe(
    switchMap(({ selected$ }) => selected$),
    map((x) => x && x.has(props$?.value?.tag?.typeKey))
  );

  return {
    setEl: (elRef) => (el = elRef),
    isHighlighted: combineLatest([highlight$, select$]).pipe(
      map(([a, b]) => a || b),
      distinctUntilChanged()
    ),
  };
}

function TagEl({ tag, children, isHighlighted, highlighted$, setEl }) {
  const elRef = useRef(null);
  const colorCl = TAG_BG[tag.tagType];

  const bgCl = isHighlighted
    ? `${colorCl} tw-bg-opacity-30 tw-bg-black tw--mx-0.5 tw-px-0.5`
    : `${colorCl} tw-bg-opacity-0`;

  useEffect(() => {
    setEl(elRef);
  }, []);

  return (
    <div
      ref={elRef}
      onMouseEnter={() => highlighted$.next({ typeKey: tag.typeKey })}
      onMouseLeave={() => highlighted$.next(null)}
      className={`${bgCl} tw-rounded-md tw-cursor-pointer tw-transition-colors tw-duration-300 tw-ease-in-out tw-inline`}
    >
      {children}
    </div>
  );
}

TagEl.propTypes = {
  setEl: PropTypes.func.isRequired,
  tag: PropTypes.instanceOf(Object),
  children: PropTypes.node,
  isHighlighted: PropTypes.bool,
  highlighted$: PropTypes.instanceOf(BehaviorSubject).isRequired,
};

TagEl.defaultProps = {
  tag: null,
  children: null,
  isHighlighted: false,
};

export default xstream(controller)(TagEl);
